import { BluetoothAdapterProvider } from '@services/bluetooth/adapter/BluetoothAdapterProvider';

const bluetoothAdapter = BluetoothAdapterProvider.get();

/**
 * Client for actioning bluetooth functionality.
 */
export class BluetoothService {
    /**
     * Calls Bluetooth pair method with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the device gets paired and
     *  rejects if there's an error.
     */
    public static pair(config: any): Promise<any> {
        return bluetoothAdapter.pair(config);
    }

    /**
     * Calls Bluetooth connect method with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the device becomes connected and
     *  rejects if there's an error.
     */
    public static connect(config: any): Promise<any> {
        return bluetoothAdapter.connect(config);
    }

    /**
     * Calls Bluetooth getDeviceInfo method with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when device information is returned and
     *  rejects if there's an error.
     */
    public static getDeviceInfo(config: any): Promise<any> {
        return bluetoothAdapter.getDeviceInfo(config);
    }

    /**
     * Calls Bluetooth pairAndConnect method which activates the UI, with the provided config.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the device is successfully paired and connected
     *  through UI and rejects if there's an error.
     */
    public static pairAndConnect(config: any): Promise<any> {
        return bluetoothAdapter.pairAndConnect(config);
    }

    /**
     * Calls Bluetooth getLastPairedDevice method which returns the last paired device of a device type.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the last paired device is retrieved from local storage and returned.
     */
    public static getLastPairedDevice(config: any): Promise<any> {
        return bluetoothAdapter.getLastPairedDevice(config);
    }

    /**
     * Calls Bluetooth setLastPairedDevice method which sets the last paired device of a device type.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when the last paired device is set in local storage.
     */
    public static setLastPairedDevice(config: any): Promise<any> {
        return bluetoothAdapter.setLastPairedDevice(config);
    }

    /**
     * Calls Bluetooth clearLastPairedDevice to clear last paired device of a device type from local storage.
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves the value is removed from local storage.
     */
    public static clearLastPairedDevice(config: any): Promise<any> {
        return bluetoothAdapter.clearLastPairedDevice(config);
    }

    /**
     * Calls Bluetooth clearLastPairedDevice to clear last paired device of all device types
     *
     * @param {object} The config object to send.
     * @return {Promise} The promise resolves when all last paired devices are cleared from local storage.
     */
    public static clearAllLastPairedDevices(config: any): Promise<any> {
        return bluetoothAdapter.clearAllLastPairedDevices(config);
    }

    /**
     * Queries the DPI of a Bluetooth-connected printer.
     *
     * @param {object} The config object containing macAddress.
     * @return {Promise} The promise resolves with { dpi: number } on success.
     */
    public static getPrinterDPI(config: any): Promise<any> {
        return bluetoothAdapter.getPrinterDPI(config);
    }
}
